import React, { useState } from 'react';
import { CreditCard, Car } from 'lucide-react';
import PhotoActionModal from '../PhotoActionModal';
import PhotoCaptureModalEnhanced from '../PhotoCaptureModalEnhanced';
import PhotoIPCameraModal from '../PhotoIPCameraModal';
import { PhotoResult } from '../../types/photo';
import { CapturePhotoType, captureConfigForPhotoType } from './photoCaptureTargets';

/**
 * One tile in a registration form's Doc. Frente / Doc. Verso / Veículo row.
 *
 * Clicking it opens the SAME source picker Identificação opens (PhotoActionModal → upload,
 * webcam, or the live IP-camera capture). It used to open AllPhotosEditModal instead — the
 * multi-column "Editar Fotos" grid where every column starts on "Sem câmera" and shows nothing
 * until a source is chosen from a dropdown. Two neighbouring tiles, the same gesture, two
 * different tools, and only one of them put a live feed in front of the operator.
 *
 * "Editar todas" still opens AllPhotosEditModal: reviewing or re-cropping every photo side by
 * side is what that modal is good at. This is about the single-tile path.
 *
 * Per-type camera category, crop and face-detection settings live in photoCaptureTargets.
 */

/** The minimum a captured photo carries; callers store richer objects than this. */
export interface ThumbPhoto {
  base64: string;
}

interface PhotoCaptureThumbProps {
  type: CapturePhotoType;
  photo: ThumbPhoto | null | undefined;
  onCapture: (photo: PhotoResult) => void;
  darkMode: boolean;
  /** Drives the red "still missing" treatment and the asterisk on the caption. */
  required?: boolean;
  disabled?: boolean;
  customerId?: string;
}

const ICONS: Record<CapturePhotoType, React.ComponentType<{ className?: string }>> = {
  document_front: CreditCard,
  document_back: CreditCard,
  vehicle: Car,
};

export const PhotoCaptureThumb: React.FC<PhotoCaptureThumbProps> = ({
  type,
  photo,
  onCapture,
  darkMode,
  required = false,
  disabled = false,
  customerId,
}) => {
  const [showActionModal, setShowActionModal] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showIPCameraModal, setShowIPCameraModal] = useState(false);
  const [uploadTab, setUploadTab] = useState<0 | 1>(0);

  const config = captureConfigForPhotoType(type);
  const Icon = ICONS[type];
  const isMissing = required && !photo;

  const openUploadWithTab = (tab: 0 | 1) => {
    setShowActionModal(false);
    setUploadTab(tab);
    setShowUploadModal(true);
  };

  const handleIPCameraResult = (result: PhotoResult) => {
    onCapture(result);
    setShowIPCameraModal(false);
  };

  const handleUploadResult = (result: PhotoResult) => {
    onCapture(result);
    setShowUploadModal(false);
  };

  return (
    <>
      <div
        data-testid="photo-capture-thumb"
        onClick={() => { if (!disabled) setShowActionModal(true); }}
        className={`aspect-[3/2] border-2 border-dashed rounded-lg flex flex-col items-center justify-center overflow-hidden transition-colors ${
          disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'
        } ${
          photo
            ? 'border-transparent'
            : isMissing
              ? darkMode ? 'border-red-700 hover:border-red-500' : 'border-red-300 hover:border-red-400 bg-red-50'
              : darkMode
                ? 'border-gray-700 hover:border-blue-500'
                : 'border-gray-300 hover:border-blue-400 hover:bg-blue-50'
        }`}
        title={`${photo ? 'Editar' : 'Adicionar'} ${config.label}`}
      >
        {photo ? (
          <img src={photo.base64} alt={config.label} className="w-full h-full object-cover" />
        ) : (
          <>
            <Icon className={`w-3.5 h-3.5 mb-0.5 ${isMissing ? (darkMode ? 'text-red-500' : 'text-red-400') : darkMode ? 'text-gray-600' : 'text-gray-400'}`} />
            <p className={`text-[8px] text-center leading-tight ${isMissing ? (darkMode ? 'text-red-400' : 'text-red-500') : darkMode ? 'text-gray-600' : 'text-gray-400'}`}>
              {config.label}{required ? ' *' : ''}
            </p>
          </>
        )}
      </div>

      {/* Source picker — identical to the one Identificação opens via PhotoSection. */}
      <PhotoActionModal
        open={showActionModal}
        onClose={() => setShowActionModal(false)}
        onUpload={() => openUploadWithTab(0)}
        onWebcam={() => openUploadWithTab(1)}
        onIPCamera={() => {
          setShowActionModal(false);
          setShowIPCameraModal(true);
        }}
        title={config.label}
        darkMode={darkMode}
      />

      {showUploadModal && (
        <PhotoCaptureModalEnhanced
          open={showUploadModal}
          onClose={() => setShowUploadModal(false)}
          onConfirm={handleUploadResult}
          title={config.label}
          cropShape={config.cropShape}
          aspectRatio={config.aspectRatio}
          customerId={customerId}
          initialTab={uploadTab}
          hideIPCamera
        />
      )}

      {showIPCameraModal && (
        <PhotoIPCameraModal
          open={showIPCameraModal}
          onClose={() => setShowIPCameraModal(false)}
          onConfirm={handleIPCameraResult}
          title={config.label}
          customerId={customerId}
          filterCategory={config.filterCategory}
          cropShape={config.cropShape}
          aspectRatio={config.aspectRatio}
          disableFaceDetection={config.disableFaceDetection}
          darkMode={darkMode}
        />
      )}
    </>
  );
};

export default PhotoCaptureThumb;
